import { formatDate } from 'date-fns';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import z from 'zod';
import type { ComboboxOption } from '../components';
import type { ConditionalDependsValueRules, CountryField, CountryFieldsResponse } from '../types';
import {
  DYNAMIC_FIELD_CALCULATION_TYPES,
  DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS,
  DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS,
  DYNAMIC_FIELD_TYPES,
  type DynamicCalculationType,
  EMPLOYEE_CONTRACT_TYPE,
} from './constants';
import {
  addMonths,
  type DateResolvableValue,
  getAdultAgeDate,
  getDayDifference,
  getFirstDayOfNextMonth,
  getMonthDifference,
  getNextWorkingDay,
  normalizeDate,
  resolveToDate,
  subtractMonths,
} from './date';
import { type NormalizeCaseRegistry, normalizeCaseMap } from './utils';

function coerceToComparableValue(input: unknown) {
  if (!input) return input;

  if (typeof input === 'object' && 'value' in input) {
    return input.value;
  }

  return input;
}

/**
 * Converts a raw form value to the string key used in `value_rules` lookups.
 * Booleans → "1" / "0", numbers → their string, strings → trimmed.
 */
function toRuleKey(value: unknown) {
  const raw = coerceToComparableValue(value);
  if (typeof raw === 'boolean') return raw ? '1' : '0';
  if (typeof raw === 'number') return String(raw);
  if (typeof raw === 'string') return raw.trim();
  return undefined;
}

/** Compares two values using the same normalization as toRuleKey so booleans,
 *  numbers, and strings are treated consistently across all equals checks. */
function isEqualsMatch(left: unknown, right: unknown): boolean {
  const normLeft = toRuleKey(left);
  const normRight = toRuleKey(right);
  return normLeft !== undefined && normLeft === normRight;
}

/**
 * Resolves the matching `ConditionalDependsValueRules` entry for a `condition: "equals"` block.
 * Falls back to `default` when no key matches.
 */
function resolveEqualsRule(
  valueRules: Record<string, ConditionalDependsValueRules>,
  sourceValue: unknown
) {
  const ruleKey = toRuleKey(sourceValue);
  if (ruleKey !== undefined && Object.hasOwn(valueRules, ruleKey)) return valueRules[ruleKey];
  return valueRules.default;
}

/**
 * Resolves a threshold-based rule map by finding the first key whose numeric value
 * is >= diff. Falls back to `default` when no threshold is matched.
 */
function resolveRangeRule<T>(rules: Record<string, T>, diff: number): T | undefined {
  const numericKeys = Object.keys(rules)
    .filter((k) => k !== 'default')
    .map(Number)
    .sort((a, b) => a - b);
  const matched = numericKeys.find((k) => diff <= k);
  return matched !== undefined ? rules[String(matched)] : rules.default;
}

/**
 * Creates a dynamic form schema by extending a base schema with country-specific fields
 * @param formSchema - The base Zod schema to extend
 * @param countryFields - Array of country-specific field configurations
 * @returns Extended Zod schema with dynamic country fields and conditional validation
 */
export function createDynamicFormSchema(
  formSchema: z.ZodObject<z.ZodRawShape>,
  countryFields?: CountryField[]
) {
  if (!countryFields || countryFields.length === 0) {
    return formSchema;
  }

  // In Zod v4, .refine() stores refinements on the ZodObject itself.
  // Calling .extend() to overwrite an existing key on a refined schema throws.
  // Skip dynamic fields whose names already exist in the base schema — those
  // fields are already validated by the base schema's own rules.
  const existingKeys = new Set(Object.keys(formSchema.shape));
  const newFields = countryFields.filter((f) => !existingKeys.has(f.name));

  // Even if there are no new fields to add we may still need to apply
  // conditional_required validation for fields already in the base schema
  // (handled by the superRefine below), so we continue rather than returning early.

  // Create dynamic fields for country-specific fields.
  // All fields are made optional at the schema level — type-level failures (e.g. z.date()
  // receiving undefined) do not set continue:true, which aborts the entire checks pipeline
  // in Zod v4 and prevents superRefine from running. Required validation is moved to
  // superRefine where errors are continuable by default.
  const countryFieldsSchema = newFields.reduce(
    (acc, field) => {
      let fieldSchema: z.ZodTypeAny;

      // Set base schema based on field type
      if (field.type === DYNAMIC_FIELD_TYPES.DATE) {
        const baseDate = z.preprocess(
          (val) => (val === '' ? undefined : val),
          z.date({ message: `${field.label} is required` })
        );
        fieldSchema = baseDate.optional().nullable();
      } else {
        fieldSchema = z.string();

        if (typeof field.max_length === 'number') {
          fieldSchema = (fieldSchema as z.ZodString).max(
            field.max_length,
            `${field.label} must be at most ${field.max_length} characters`
          );
        }

        if (typeof field.min_length === 'number') {
          fieldSchema = (fieldSchema as z.ZodString).min(
            field.min_length,
            `${field.label} must be at least ${field.min_length} characters`
          );
        }

        fieldSchema = z.preprocess(
          (val) => (val === '' ? undefined : val),
          (fieldSchema as z.ZodString).optional()
        );
      }

      if (field.type === DYNAMIC_FIELD_TYPES.SELECT) {
        if (field.options && Object.keys(field.options).length > 0) {
          const optionKeys = Object.keys(field.options) as [string, ...string[]];
          fieldSchema = z.preprocess(
            (val) => (val === '' ? undefined : val),
            z.enum(optionKeys).optional()
          );
        } else {
          fieldSchema = z.preprocess(
            (val) => (val === '' ? undefined : val),
            z.string().optional()
          );
        }
      }

      acc[field.name] = fieldSchema;
      return acc;
    },
    {} as Record<string, z.ZodTypeAny>
  );

  // Only call .extend() when there are genuinely new fields to add
  const extendedSchema =
    Object.keys(countryFieldsSchema).length > 0
      ? formSchema.extend(countryFieldsSchema)
      : formSchema;

  // Required validation lives here because schema-level invalid_type errors lack
  // continue:true in Zod v4 and would abort the checks pipeline. Includes override
  // fields too — those are not in newFields (their name already exists in the base
  // schema), but the base only types them as `.optional()` and does not enforce
  // required-ness, so without this they'd silently pass when empty.
  const fieldsRequiredUnconditionally = countryFields.filter(
    (field) => field.required && !field.conditional_required
  );
  // Conditional validation covers all dynamic fields, including those that
  // already exist in the base schema (e.g. a dynamic endDate with conditional_required).
  const fieldsWithConditionalRequired = countryFields.filter((field) => field.conditional_required);
  const fieldsWithConditionalDepends = countryFields.filter((field) => field.conditional_depends);

  if (
    fieldsRequiredUnconditionally.length === 0 &&
    fieldsWithConditionalRequired.length === 0 &&
    fieldsWithConditionalDepends.length === 0
  ) {
    return extendedSchema;
  }

  return extendedSchema.superRefine((formData, ctx) => {
    if (fieldsRequiredUnconditionally.length > 0) {
      fieldsRequiredUnconditionally.forEach((field) => {
        const fieldValue = formData[field.name];
        const isEmpty =
          field.type === DYNAMIC_FIELD_TYPES.DATE
            ? fieldValue === '' || fieldValue === undefined || fieldValue === null
            : !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim().length === 0);

        if (isEmpty) {
          ctx.addIssue({
            code: 'custom',
            message: `${field.label} is required`,
            path: [field.name],
          });
        }
      });
    }

    if (fieldsWithConditionalRequired.length > 0) {
      // Conditional required fields
      fieldsWithConditionalRequired.forEach((field) => {
        const conditional = field.conditional_required!;

        if (
          conditional.condition === DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.EQUALS &&
          isEqualsMatch(formData[conditional.field], conditional.value)
        ) {
          // If condition is met, the field becomes required
          const fieldValue = formData[field.name];

          // Handle different field types for validation
          let isFieldEmpty = false;

          if (field.type === DYNAMIC_FIELD_TYPES.DATE) {
            // For date fields, check if the value is undefined, null, or invalid date
            isFieldEmpty = fieldValue === '' || fieldValue === undefined || fieldValue === null;
          } else {
            // For text/select fields, check if empty string or undefined/null
            isFieldEmpty =
              !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim().length === 0);
          }

          if (isFieldEmpty) {
            ctx.addIssue({
              code: 'custom',
              message: `${field.label} is required`,
              path: [field.name],
            });
          }
        }
      });
    }

    if (fieldsWithConditionalDepends.length > 0) {
      fieldsWithConditionalDepends.forEach((field) => {
        const conditional = field.conditional_depends!;
        if (conditional.condition !== DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.EQUALS) return;

        const sourceFieldName = conditional.fields[0];
        if (!sourceFieldName) return;

        const fieldRule = resolveEqualsRule(conditional.value_rules, formData[sourceFieldName]);
        if (!fieldRule) return;

        const fieldValue = formData[field.name];
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') return;

        const len = String(fieldValue).length;

        if (typeof fieldRule.min_length === 'number' && len < fieldRule.min_length) {
          ctx.addIssue({
            code: 'custom',
            message: `${field.label} must be at least ${fieldRule.min_length} characters`,
            path: [field.name],
          });
        }

        if (typeof fieldRule.max_length === 'number' && len > fieldRule.max_length) {
          ctx.addIssue({
            code: 'custom',
            message: `${field.label} must be at most ${fieldRule.max_length} characters`,
            path: [field.name],
          });
        }
      });
    }
  });
}

/**
 * Determines if a dynamic field should be displayed based on conditional requirements
 * @param field - The country field configuration
 * @param form - React Hook Form instance to watch field values
 * @returns True if the field should be shown, false otherwise
 */
export function shouldShowField(field: CountryField, form: UseFormReturn) {
  if (!field.conditional_required) {
    return true; // Show field if no conditional requirements
  }

  const { field: conditionField, condition, value } = field.conditional_required;

  if (condition === DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.EQUALS) {
    return isEqualsMatch(form.watch(conditionField), value);
  }

  return true; // Default to showing the field
}

/**
 * Builds additional data object from dynamic country fields form data
 * @param countryFieldsForCountry - Array of country field configurations
 * @param formData - Form data containing field values
 * @returns Object with formatted field values for submission
 */
export function buildAdditionalData(
  countryFieldsForCountry: CountryField[],
  formData: FieldValues
) {
  const additionalData: Record<string, string> = {};

  // Add all dynamic country fields
  if (countryFieldsForCountry && formData) {
    countryFieldsForCountry.forEach((field) => {
      const fieldValue = formData[field.name as keyof typeof formData];
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
        return;
      }

      if (fieldValue instanceof Date) {
        additionalData[field.name] = formatDate(fieldValue, 'yyyy-MM-dd');
      } else if (typeof fieldValue === 'object' && 'value' in fieldValue && fieldValue.value) {
        additionalData[field.name] = (fieldValue as ComboboxOption).value;
      } else {
        additionalData[field.name] = fieldValue.toString();
      }
    });
  }

  return additionalData;
}

// Value type used by dynamic fields in form state.
export type DynamicFormValue = string | Date | null | undefined;

// Map of dynamic field values keyed by country field name.
export type DynamicFormValues = Record<string, DynamicFormValue>;

export function getDefaultValuesForDynamicFields(fields: CountryField[], formData?: FieldValues) {
  return (
    fields.reduce<DynamicFormValues>((acc, field) => {
      const existing = formData?.[field.name];

      if (field.type === DYNAMIC_FIELD_TYPES.DATE) {
        acc[field.name] = resolveToDate(existing);
      } else {
        acc[field.name] = (existing as string) ?? '';
      }

      return acc;
    }, {}) || {}
  );
}

/**
 * Determines if a given end date should be disabled in the date picker.
 *
 * Business rules:
 * 1. End date must be after the start date
 * 2. For fixed contracts: End date must be at least 3 months after the start date (enforces 3-month minimum contract duration)
 *
 * @param date - The date to check
 * @param startDate - The selected start date (optional)
 * @param contractType - The contract type (permanent or fixed)
 * @returns true if the date should be disabled, false otherwise
 */
export function isEndDateDisabled(date: Date, startDate: Date | undefined, contractType: string) {
  if (!startDate) {
    return false;
  }

  const start = normalizeDate(startDate);
  const currentDate = normalizeDate(date);

  // For fixed contracts, enforce 3-month minimum duration
  if (contractType === EMPLOYEE_CONTRACT_TYPE.FIXED) {
    const threeMonthsAfterStart = addMonths(start, 3);
    // Disable dates before or equal to start date, and dates within 3 months after start
    return currentDate <= start || currentDate < threeMonthsAfterStart;
  }

  // For other contracts, just disable start date and any date before it
  return currentDate <= start;
}

/**
 * Determines if a given start date should be disabled in the date picker.
 *
 * Business rules:
 * 1. If current day < 20th: Start date must be at least 5 working days from today (6th working day available)
 * 2. If current day >= 20th: Start date must be in the next month or later
 * 3. Start date cannot be after the end date (if end date is selected)
 * 4. For fixed contracts: Start date cannot be within 3 months before the end date (enforces 3-month minimum contract duration)
 *
 * @param date - The date to check
 * @param endDate - The selected end date (optional)
 * @param contractType - The contract type (permanent or fixed, optional)
 * @returns true if the date should be disabled, false otherwise
 */
export function isStartDateDisabled(date: Date, endDate?: Date, contractType?: string) {
  const today = normalizeDate(new Date());
  const currentDay = today.getDate();

  // Rule 1 & 2: Handle minimum start date based on current day of month
  if (currentDay < 20) {
    // If before 20th, first available day is today + 5 working days
    const nextWorkingDay = getNextWorkingDay();
    if (date < nextWorkingDay) {
      return true;
    }
  } else {
    // If 20th or later, start date must be in next month
    const firstDayNextMonth = getFirstDayOfNextMonth();
    if (date < firstDayNextMonth) {
      return true;
    }
  }

  // Rule 3 & 4: Handle end date constraints
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    // For fixed contracts, enforce 3-month minimum duration
    if (contractType === EMPLOYEE_CONTRACT_TYPE.FIXED) {
      const threeMonthsBeforeEnd = subtractMonths(end, 3);
      // Disable dates that are after end date or too close to it (< 3 months gap)
      return date > end || date > threeMonthsBeforeEnd;
    }

    // For other contracts, just disable dates after end date
    return date > end;
  }

  return false;
}

/**
 * Normalised constraints produced by resolving a field's `conditional_depends` config
 * against the current form values. Components consume this instead of inspecting
 * `conditional_depends` directly.
 */
export interface ResolvedConstraints {
  /** Upper-bound date for DATE fields — derived from `date_value_rules`. */
  dependantMaxDate?: Date;
  /** Allowed option value keys for SELECT fields — derived from `select_value_rules`. */
  allowedOptionKeys?: string[];
  /** Active validation bounds for TEXT fields — derived from `value_rules`. */
  validationRules?: ConditionalDependsValueRules;
}

/**
 * Resolves a field's `conditional_depends` config against the current watched values,
 * returning a normalised `ResolvedConstraints` object.
 *
 * The component passes raw watched values (`dependsValues[i]` corresponds to
 * `field.conditional_depends.fields[i]`) and a `resolveDate` converter for the
 * date-range branch. Adding support for a new condition type means extending this
 * function only — no component changes are required.
 */
export function resolveFieldConstraints(
  field: CountryField,
  dependsValues: DateResolvableValue[],
  resolveDate: (val: DateResolvableValue) => Date | undefined
) {
  const result: ResolvedConstraints = {};
  const depends = field.conditional_depends;
  if (!depends) return result;

  if (depends.condition === DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.EQUALS) {
    const rules = resolveEqualsRule(depends.value_rules, dependsValues[0]);

    if (rules) {
      result.validationRules = rules;
    }
  } else if (depends.condition === DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.LESS_THAN) {
    const fromDate = resolveDate(dependsValues[0]);
    const toDate = resolveDate(dependsValues[1]);

    if (!fromDate || !toDate) {
      const defaultSelectValues = depends.select_value_rules?.default;
      if (defaultSelectValues) {
        result.allowedOptionKeys = defaultSelectValues.map(String);
      }

      return result;
    }

    const diff =
      depends.unit === 'days'
        ? getDayDifference(fromDate, toDate)
        : getMonthDifference(fromDate, toDate);

    if (depends.date_value_rules) {
      const offsetDays = resolveRangeRule(depends.date_value_rules, diff);
      if (offsetDays !== undefined) {
        const maxDate = new Date(fromDate);
        maxDate.setDate(maxDate.getDate() + offsetDays);
        result.dependantMaxDate = maxDate;
      }
    }

    if (depends.select_value_rules) {
      const allowedValues = resolveRangeRule(depends.select_value_rules, diff);
      if (allowedValues) result.allowedOptionKeys = allowedValues.map(String);
    }
  }

  return result;
}

export interface DateConstraintContext {
  startDate?: Date;
  dependantMaxDate?: Date;
  minMonths?: number;
  maxMonths?: number;
}

/**
 * Returns true if `date` falls outside the allowed range for a dynamic date
 * field constrained by a reference start date and optional month bounds.
 * Generic — used for end date, probation period end, and any future ranged
 * date fields.
 */
export function isDynamicDateDisabled(date: Date, ctx: DateConstraintContext) {
  const current = normalizeDate(date);
  const isAfterComputedMax = !!ctx.dependantMaxDate && current > ctx.dependantMaxDate;

  if (!ctx.startDate) return isAfterComputedMax;

  const start = normalizeDate(ctx.startDate);
  const minEnd = ctx.minMonths !== undefined ? addMonths(start, ctx.minMonths) : undefined;
  const maxEnd = ctx.maxMonths !== undefined ? addMonths(start, ctx.maxMonths) : undefined;
  const isBeforeMin = !!minEnd && current < minEnd;
  const isAfterMax = !!maxEnd && current > maxEnd;

  return isBeforeMin || isAfterComputedMax || isAfterMax;
}

/**
 * Returns true if `date` is not a valid birth date, enforcing two rules:
 *
 * 1. Future dates — a birthday cannot be in the future.
 * 2. Minimum age of 18 — NativeTeams issues formal employment contracts.
 *    Onboarding a minor would require special offline handling, so we block it at the UI level.
 */
export function isBirthDateDisabled(date: Date): boolean {
  const today = normalizeDate(new Date());
  const current = normalizeDate(date);

  if (current > today) return true;

  const oldestAllowed = getAdultAgeDate();

  return current > oldestAllowed;
}

type IsDateDisabledFn = (date: Date, ctx?: DateConstraintContext) => boolean;

/**
 * Registry of field-name-specific disabled-date rules for dynamic date fields.
 * Add a new entry here to apply a bespoke rule to any future date field —
 * the DynamicDateField component resolves this map automatically and requires
 * no changes.
 */
export const DATE_FIELD_DISABLED_RULES: Record<string, IsDateDisabledFn> = {
  start_date: (date) => isStartDateDisabled(date),
  birth_date: (date) => isBirthDateDisabled(date),
};

/** Options for {@link getDisabledDate}. */
export interface GetDisabledDateOptions {
  /**
   * Keys matching `DATE_FIELD_DISABLED_RULES` (e.g. `start_date`, `birth_date`) whose
   * bespoke rule should be skipped so callers can relax constraints (e.g. Admin Hub backdating).
   */
  skipNamedRulesForFields?: string[];
}

/**
 * Resolves a disabled-date function for a dynamic date field.
 *
 * Resolution order:
 * 1. Field has a bespoke entry in DATE_FIELD_DISABLED_RULES (e.g. start_date, birth_date)
 * 2. Field has config-driven range / dependant-max constraints → generic isDynamicDateDisabled
 *    (e.g. end_date, probation_period_end_date)
 * 3. No constraints → undefined (picker has no disabled dates)
 */
export function getDisabledDate(
  field: string,
  ctx: DateConstraintContext,
  options?: GetDisabledDateOptions
): IsDateDisabledFn | undefined {
  const namedRule = DATE_FIELD_DISABLED_RULES[field];
  const skipNamedRule = options?.skipNamedRulesForFields?.includes(field) ?? false;
  if (namedRule && !skipNamedRule) {
    return (date) => namedRule(date);
  }

  const hasRange =
    ctx.startDate !== undefined && (ctx.minMonths !== undefined || ctx.maxMonths !== undefined);
  const hasDepends = ctx.dependantMaxDate !== undefined;

  if (hasRange || hasDepends) {
    return (date) => isDynamicDateDisabled(date, ctx);
  }

  return undefined;
}

export function getDefaultSelectedMonth(field: string, ctx: DateConstraintContext) {
  if (ctx.startDate) return addMonths(ctx.startDate, ctx.minMonths ?? 1);
  if (field === 'birth_date') return getAdultAgeDate();
  return undefined;
}

export function dynamicFieldCalculate(
  startDate: Date,
  calculation: {
    type: DynamicCalculationType;
    years?: number;
    days?: number;
  }
) {
  const normalizedStartDate = normalizeDate(startDate);

  if (calculation.type === DYNAMIC_FIELD_CALCULATION_TYPES.ADD_YEARS_SUBTRACT_DAYS) {
    const calculatedDate = new Date(normalizedStartDate);
    calculatedDate.setFullYear(calculatedDate.getFullYear() + (calculation.years ?? 0));
    calculatedDate.setDate(calculatedDate.getDate() - (calculation.days ?? 0));

    return normalizeDate(calculatedDate);
  }

  return normalizedStartDate;
}

export function normalizeCountryField(
  field: CountryField,
  normalizeCaseRegistry: NormalizeCaseRegistry
): CountryField {
  const shouldNormalizeName = field.override ?? false;

  const normalizedField: CountryField = {
    ...field,
    name: shouldNormalizeName ? normalizeCaseMap[normalizeCaseRegistry](field.name) : field.name,
  };

  if (field.start_date_ref) {
    normalizedField.start_date_ref = normalizeCaseMap[normalizeCaseRegistry](field.start_date_ref);
  }

  if (field.conditional_required) {
    normalizedField.conditional_required = {
      ...field.conditional_required,
      field: shouldNormalizeName
        ? normalizeCaseMap[normalizeCaseRegistry](field.conditional_required.field)
        : field.conditional_required.field,
    };
  }

  if (field.conditional_depends) {
    normalizedField.conditional_depends = {
      ...field.conditional_depends,
      fields: field.conditional_depends.fields.map(normalizeCaseMap[normalizeCaseRegistry]),
    };
  }

  return normalizedField;
}

export function normalizeCountryFieldsResponse(
  countryFields: CountryFieldsResponse | null,
  normalizeCaseRegistry: NormalizeCaseRegistry
): CountryFieldsResponse {
  if (!countryFields) {
    return {};
  }

  return Object.entries(countryFields).reduce<CountryFieldsResponse>(
    (acc, [countryIsoCode, data]) => {
      acc[countryIsoCode] = {
        ...data,
        fields: data.fields.map((field) => normalizeCountryField(field, normalizeCaseRegistry)),
      };
      return acc;
    },
    {}
  );
}
